import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject, take, takeUntil, filter, combineLatest, firstValueFrom } from 'rxjs';
import { MdbModalRef, MdbModalService } from 'mdb-angular-ui-kit/modal';
import { UploadArtModalComponent } from '@features/catalog/product-design/upload-art-modal/upload-art-modal.component';
import { setActivePosition, setDesignObject, updateDesignNameObject, addDesign, updateDesinPositionObject, setMobileView, removeFileFromDesignLibrary, getClientDesigns } from '@core/state/design-tool/design-tool.actions';
import * as fromCatalog from '@core/state/catalog/catalog.selectors';
import * as fromDesignTool from '@core/state/design-tool/design-tool.selectors';
import { DesignToolPosition, Designs, PositionDesign, TransformedFiles, VariantImages, VariantColors, DesignToolCanvas, DecorationTypeEnum } from '@core/models/designTool';
import { SessionService } from '@core/services/session/session.service';
import { CustomizeProductModalComponent } from './customize-product-modal/customize-product-modal.component';
import { CatalogDesignService } from '@core/services/catalog/catalog-design.service';
import { EditDesignNameModalComponent } from './edit-design-name-modal/edit-design-name-modal.component';
import { CanvasService } from '@core/services/fabric/canvas.service';
import { ColorsCountService } from '@core/services/colors/colors-count.service';
import { ErrorsService } from '@core/services/errors/errors.service';
import { MetaService } from '@core/services/seo/meta-tag-service';
import { isArray, isObject } from 'underscore';
import { AuthService, User } from '@auth0/auth0-angular';
import { analyzeImage } from '@shared/utilities/file.utils';

@Component({
  selector: 'pd-product-design',
  templateUrl: './product-design.component.html',
  styleUrls: ['./product-design.component.scss'],
})
export class ProductDesignComponent implements OnInit,OnDestroy{
  // product-design-library component params
  designFiles: TransformedFiles[] = [];
  designMockups$: Observable<VariantImages> = this.store.select(fromDesignTool.selectDesignToolMockups);
  canvasDimensions$: Observable<DesignToolCanvas> = this.store.select(fromDesignTool.selectCanvasDimensions);
  canvasDimensions: DesignToolCanvas;
  user: User;
  clientDesigns$: Observable<Designs[]> = this.store.select(fromDesignTool.selectClientDesigns);
  selectCurrentPositionData$: Observable<Partial<DesignToolPosition>> = this.store.select(fromDesignTool.selectCurrentPositionData);
  designObject$: Observable<Designs> = this.store.select(fromDesignTool.selectDesignObject);
  designObject: Designs
  productVariants$ = this.store.select(fromCatalog.selectProductVariant);
  productVariants: any = [];
  selectedProductVariant: VariantColors = null;
  selectedPosition: string = 'front';
  selectedDesignImage: string;
  priceChanged: any;
  brand!: string;
  style!: string;
  color!: string;
  product!: {brand: string, style: string, color: string};
  decorationType: DecorationTypeEnum;
  productSizes: VariantColors[] = null;
  destroy$ = new Subject<void>()
  client: any;
  productVariantColor$ = this.store.select(fromCatalog.selectProductVariantColor);
  customizeModalRef: MdbModalRef<CustomizeProductModalComponent> | null = null;
  designNameModalRef: MdbModalRef<EditDesignNameModalComponent> | null = null;
  designId: string;
  clientDesigns: Designs[];
  cartId: string;
  canCustomize$: Observable<boolean> = this.store.select(fromDesignTool.selectCanCustomize).pipe(filter(e => e != undefined));
  canCustomize: boolean;// we only need this for using it in handleImageClick() to prevent the user to add images to the canvas in case this property's value is false
  mobileView: boolean;

  constructor(
    private errorsService: ErrorsService,
    private modalService: MdbModalService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
    private sessionService: SessionService,
    private readonly catalogDesignService: CatalogDesignService,
    private canvasService: CanvasService,
    private colorsCountService: ColorsCountService,
    private metaService: MetaService,
    private auth: AuthService
  ) {
    this.productVariants$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if(data && data.length > 0){
        this.productVariants = data;
        // check if color is set
        if(this.color){
          data = this.productVariants.filter(item => item.colorName === this.color);
          // Safety check: If, for some reason, filtering by color resulted in no matches,
          // fall back to the first item in the original data
          if(data.length === 0){
            data = [this.productVariants[0]];
          }
        }else {
          data = [this.productVariants[0]];
        }
        this.selectedProductVariant = data[0];
        const removeFromUrl = (url: string) => {
          return url?.replace(/Images\/Style\//, '');
        }
        if (this.selectedProductVariant && this.selectedProductVariant.images) {
          const updatedImages = {};

          for (let key in this.selectedProductVariant.images) {
            if (this.selectedProductVariant.images[key]) {
              updatedImages[key] = removeFromUrl(this.selectedProductVariant.images[key]);
            } else {
              updatedImages[key] = this.selectedProductVariant.images[key];
            }
          }

          this.selectedProductVariant = { ...this.selectedProductVariant, images: updatedImages as VariantImages };
        }
      }
    });
    this.productVariantColor$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.productSizes = data
    })
  }

  ngOnInit(): void {
    this.client = this.sessionService.getActiveClient();
    this.store.dispatch(getClientDesigns({client: this.sessionService.getActiveClient()}))
    this.store.select(fromDesignTool.selectDesignFilesTransformed).pipe(takeUntil(this.destroy$)).subscribe(files => {
      this.designFiles = files.map(e => analyzeImage(e))
    })
    combineLatest([this.route.queryParams, this.clientDesigns$, this.productVariantColor$])
    .pipe(
      takeUntil(this.destroy$),
      filter(([queryParams, clientDesigns, productVariantColors]) => !!clientDesigns && productVariantColors?.length > 0)
    )
    .subscribe(async ([queryParams, clientDesigns, productVariantColors]) => {
      this.productSizes = productVariantColors
      this.brand = this.route.snapshot.params.brand;
      this.style = this.route.snapshot.params.style;
      this.product = {
        brand: this.brand,
        style: this.style,
        color: ''
      }
      this.metaService.updateMetaTags(
        `Customize ${this.brand} ${this.style} in the Design Tool`,
        `Customize your own products such as ${this.brand} ${this.style} using the Design Tool on the Culture Studio Platform`,
        `Customize your own products such as ${this.brand} ${this.style} using the Design Tool on the Culture Studio Platform`,
        `https://img.stokkup.com/app/cs-circle-logo.png`,
      )

      this.clientDesigns = clientDesigns
      // if a design id is already defined and a designs collection is available
      if (queryParams?.color && queryParams?.design && this.clientDesigns) {
        this.color = queryParams.color;
        this.product.color = this.color
        this.designId = queryParams.design;
        this.cartId = queryParams.cart
        setTimeout(() => this.checkCurrentDesign(), 5000)
      } else if (!queryParams?.design || !queryParams?.color) {
        this.canvasService.disposeCanvas()
        this.store.dispatch(setActivePosition({activePosition: 'back'}));
        this.store.dispatch(setActivePosition({activePosition: 'front'}));
        const item: VariantColors = productVariantColors?.[0];
        const date = new Date()
        const designName = `${date.getMonth() < 10 ? 0 : ''}${date.getMonth()}-${date.getDate() < 10 ? 0 : ''}${date.getDate()}-${date.getFullYear()} ${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}_Design`
        this.user = await firstValueFrom(this.auth.user$)
        const design: Designs = this.catalogDesignService.formatDesignObject(designName, item, this.user)
        this.store.dispatch(addDesign({design, user: this.user}))
      }
    })

    this.designObject$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      // whenever the design object is updated
      if (data?._id) {
        this.designObject = data
        this.customizeModalRef?.close()
        this.designId = data._id
        const urlParameters = {...this.route.snapshot.queryParams};
        if (!urlParameters.design) {
          urlParameters.design = this.designObject._id;
          this.router.navigate([], { relativeTo: this.route, queryParams: urlParameters });
        }
      }
    })

    this.canvasDimensions$.pipe(takeUntil(this.destroy$)).subscribe((dimensions: DesignToolCanvas) => {
      this.canvasDimensions = dimensions
    })

    this.canCustomize$.pipe(takeUntil(this.destroy$)).subscribe((canCustomize: boolean) => {
      this.canCustomize = canCustomize
    })

    this.checkScreenSize();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    // Check the window size whenever it's resized
    this.checkScreenSize();
  }

  async checkCurrentDesign() {
    // if the defined design id exists in the designs collection
    const design = this.clientDesigns.find(e => e._id == this.designId)
    const client = this.sessionService.getActiveClient()
    if (design) {
      // hide the 404 error screen in case it is being shown
      this.errorsService.hideError()
      // update the design object in the store
      this.store.dispatch(setDesignObject({designObject: design}))
    } else {
      console.error('design not found in the current designs list!')
      const params: any = await firstValueFrom(this.route.params)
      const queryParams: any = await firstValueFrom(this.route.queryParams)
      const redirect = `/catalog/${params.brand}/${params.style}/customize?color=${queryParams.color}&design=${queryParams.design}`
      // show the 404 error screen
      this.errorsService.setErrorState({
        imageUrl: 'assets/images/oops-t-shirt.png',
        title: 'Design not found',
        message: `The design <strong>${this.designId}</strong> doesn't exist or is not available for the client <strong>${client.name}</strong>.`,
        buttons: [
          {
            routerLink: `/login/choose-client`,
            routerParams: {redirect},
            text: 'Change Client'
          }
        ]
      })
      this.errorsService.showError()
    }
  }

  editDesignNameModal() {
    this.designNameModalRef = this.modalService.open(EditDesignNameModalComponent, {
      modalClass: 'modal-dialog-centered',
      data: {
        designName: this.designObject.name
      }
    });

    this.designNameModalRef.onClose.subscribe((name: any) => {
      if(name) {
        this.store.dispatch(updateDesignNameObject({name: name}))
      }
     });
  }

  checkScreenSize(): void {
    // Update the showTemplate variable based on screen size
    if(window.innerWidth <= 900) {
      this.mobileView = true
      this.store.dispatch(setMobileView({mobileView: true}))
    } else {
      this.mobileView = false
      this.store.dispatch(setMobileView({mobileView: false}))
    }
  }

  goBack(){
    this.router.navigate(['../'], { relativeTo: this.route, replaceUrl: true, queryParams: []})
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  changePosition(event){
    this.selectedPosition = event;
    this.store.dispatch(setActivePosition({activePosition: event}));
  }

  selectedDecorationType(event) {
    this.decorationType = event
  }

  priceChanges(event){
    this.priceChanged = {status: event, timestamp: Date.now()};
  }

  /**
   * Open the upload art modal (product-design-library component)
   */
  openUploadArtModal(files?: File[]){
    this.modalService.open(UploadArtModalComponent, {
      modalClass: 'modal-xl modal-h-800',
      data: { client: this.client, inputFiles: files },
      ignoreBackdropClick: true
    })
  }

  /**
   * Function that will be called when the user add designs from the design library component into the canvas
   * @param event
   */
  async handleImageClick(data?: { files: TransformedFiles[], data?: { imageWidth: number; imageHeight: number; width: number; height: number; left: number; top: number}}) {
    let counter = 0
    const images = []
    const callback = async (resp: {img: fabric.Image, imageInfo: PositionDesign}) => {
      // we count every callback execution
      counter++
      // only when an image is place without drag and drop and the image is valid (not repeated in canvas)
      if (resp?.img) {
        // we update scales since they could be changed after being added into the canvas
        resp.imageInfo.scaleX = resp.img.scaleX
        resp.imageInfo.scaleY = resp.img.scaleY
        resp.imageInfo.width = resp.img.width
        resp.imageInfo.height = resp.img.height
        // we save all added images
        images.push(resp.imageInfo)
      }
      // once all petitions are processed we run only one request to update the design object in the backend
      if (counter == data.files.length) {
        // we get the current canvas elements saved in the store 
        const selectCurrentPositionData = await firstValueFrom(this.selectCurrentPositionData$)
        // we trigger the action for hit the backend and update canvas elements
        this.store.dispatch(updateDesinPositionObject({ position: this.selectedPosition, design: [...(selectCurrentPositionData?.canvas ?? []), ...images] }));
      }
    }

    data?.files?.forEach((file) => {
      this.addImageToCanvasFromLibrary(file, data.data).then(callback)
    })
  }

  onRemoveImage(fileName: string) {
    this.store.dispatch(removeFileFromDesignLibrary({fileName}))
  }

  async addImageToCanvasFromLibrary(file: TransformedFiles, data?: { imageWidth: number; imageHeight: number; width: number; height: number; left: number; top: number}): Promise<{img: fabric.Image, imageInfo: PositionDesign}|null> {
    const scale = file.fromDrop ? data.width / data.imageWidth : 0.5
    const canvasHeight = this.canvasService.canvas.height
    const canvasWidth = this.canvasService.canvas.width
    const colorsArray = await this.colorsCountService.getImageColors(file.imageUrl)
    const mobileView: boolean = await firstValueFrom(this.store.select(fromDesignTool.selectMobileView))
    const imageInfo = {
      url: file.imageUrl,
      coordinates: { x: canvasWidth/2, y: canvasHeight/2 },
      rotation: 0,
      scaleX: scale,
      scaleY: scale,
      scaledToMobile: mobileView,
      style: 'circle',
      cornerSize: 7,
      cornerColor: '#5EA7EF',
      fill: '#5EA7EF',
      borderColor: '#5EA7EF',
      borderDashArray: [5],
      // rotatingPointOffset: 30,
      // padding: 15,
      editable: true,
      colorCount: colorsArray?.length ?? 0,
      // decorationType: 'Direct to Garment', In case we want to handle decoration type per image
      shouldResize: false,
      originX: 'center',
      originY: 'center'
    } as PositionDesign;
    
    const selectCurrentPositionData = await firstValueFrom(this.selectCurrentPositionData$)
    // Check if the imageUrl already exists for this position.
    const found = selectCurrentPositionData?.canvas?.some(e => e.url == imageInfo.url)
    if (!found && this.canCustomize) {
      // first time we add an image into the canvas, we save the product image width in the object to be added to the canvas to be used after for doing size and position calculations
      imageInfo.imageClientWidth = this.canvasService.productImageClientWidth
      return this.canvasService.addImageToCanvas(imageInfo, undefined, undefined, !file.fromDrop)
    }
    return new Promise((resolve, reject) => resolve(null))
  }
}

